import { Box, Skeleton, Typography, Tooltip} from '@mui/material';
import React from 'react';
import dashboardStyles from '../styles';
import { theme } from '../../../ThemeContext/ThemeObject';
import { useQuery } from '@tanstack/react-query';
import InsertDriveFileRoundedIcon from '@mui/icons-material/InsertDriveFileRounded';
import ThumbUpAltRoundedIcon from '@mui/icons-material/ThumbUpAltRounded';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import { IKpiItems } from '../types';
import Api from '../API';

interface IProps {
  onClick?: () => void;
  type: keyof IKpiItems;
}

function KpiItem({ onClick, type }: IProps) {
  const { icon, iconColor, title, link, tooltip } = getDetails(type);
  const { isLoading, isSuccess, data } = useQuery({
    queryKey: ['kpiDashboard'],
    queryFn: Api.getKpiItems
  });

  function handleRedirect() {
    window.location.href = `/admin/${link}`;
  }

  function fluctationDailyColor(fluctuation_daily: number): string {
    let color = null;
    if (fluctuation_daily > 0) {
      color = theme.palette.success.main;
    } else if (fluctuation_daily == 0) {
      color = theme.palette.secondary.yellow;
    } else {
      color = theme.palette.error.main;
    }
    return color;
  }

  function fluctationDailyValue(fluctuation_daily: number): string {
    if (fluctuation_daily > 0) {
      return `+ ${fluctuation_daily}`;
    } else if (fluctuation_daily == 0) {
      return `0`;
    } else {
      return `${fluctuation_daily}`;
    }
  }

  return (
    <Box
      id={`kpi-${type}`}
      onClick={onClick || handleRedirect}
      sx={{ ...dashboardStyles.section, ...dashboardStyles.kpiSection, ...styles.section }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="flex-start">
        <Box sx={{ backgroundColor: `${iconColor}33`, borderRadius: '6px' }} p={1}>
          {icon}
        </Box>
        {isLoading && <Skeleton animation="wave" width={30} height={30} />}
        {isSuccess && data[type]?.fluctuation_daily !== undefined && (
          <Tooltip title="Daily Fluctuation = Today's Total - Yesterday's Total">
            <Box
              sx={{
                          ...styles.fluctuation,
                          color: fluctationDailyColor(data[type]?.fluctuation_daily)
                        }}>
              {fluctationDailyValue(data[type]?.fluctuation_daily)}
            </Box>
          </Tooltip>
        )}
      </Box>
      <Box>
        <Box style={styles.value}>
          {isLoading && <Skeleton animation="wave" width={70} height={60} />}
          {isSuccess &&
            (data[type]?.value.toLocaleString() ? data[type]?.value.toLocaleString() : 0)}
        </Box>
        <Box style={styles.title}>
            <Tooltip title={tooltip} placement="top">
                <span>{title}</span>
            </Tooltip>
        </Box>
      </Box>
    </Box>
  );
}

function getDetails(type: keyof IKpiItems): {
  title: string;
  icon: JSX.Element;
  iconColor: string;
  link: string;
  tooltip: React.ReactNode;
} {
  if (type === 'applications')
    return {
      title: 'New Applications',
      icon: <InsertDriveFileRoundedIcon sx={{ color: '#F478FF' }} fontSize="small" />,
      iconColor: '#F478FF',
      link: 'candidates',
      tooltip: 'Total is based on published jobs'
    };
  if (type === 'approvals')
    return {
      title: 'Pending Approvals',
      icon: <ThumbUpAltRoundedIcon sx={{ color: '#91CAFF' }} fontSize="small" />,
      iconColor: '#91CAFF',
      link: 'requisitions',
      tooltip: 'Total is based on active approvals, excluding archived ones'
    };
  return {
    title: `Today's Events`,
    icon: <CalendarMonthRoundedIcon sx={{ color: '#FFCC68' }} fontSize="small" />,
    iconColor: '#FFCC68',
    link: 'calendar/events',
    tooltip: "Based on active event types, that you've been invited, or that you've created"
  };
}

const styles = {
  section: {
    transition: 'box-shadow 0.3s ease-in-out',
    justifyContent: 'space-between',
    '&:hover': {
      boxShadow: '0px 10px 15px -3px rgba(0,0,0,0.05)',
      cursor: 'pointer'
    }
  },
  value: {
    fontSize: '36px',
    fontWeight: 'bold',
    color: '#333'
  },
  title: {
    color: '#333',
    fontWeight: '600',
    fontSize: '16px'
  },
  fluctuation: {
    fontWeight: 'bold',
    fontSize: '20px'
  }
};

export default KpiItem;
